import React from 'react';
import { format } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPinIcon, TruckIcon, CheckCircleIcon, ClockIcon, ExclamationTriangleIcon, XCircleIcon, PhoneIcon } from '@heroicons/react/24/outline';
import ShipmentProgressBar from './ShipmentProgressBar';

const TrackingResult = ({ trackingData }) => {
  const data = trackingData.data;
  const { t } = useLanguage();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="h-5 w-5 text-[#16A34A]" />;
      case 'exception':
      case 'on_hold':
        return <ExclamationTriangleIcon className="h-5 w-5 text-[#DC2626]" />;
      case 'cancelled':
      case 'returned':
        return <XCircleIcon className="h-5 w-5 text-[#DC2626]" />;
      case 'out_for_delivery':
      case 'in_transit':
        return <TruckIcon className="h-5 w-5 text-[#2563EB]" />;
      default:
        return <ClockIcon className="h-5 w-5 text-[#F59E0B]" />;
    }
  };

  const formatStatus = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const safe = (val) => val || t.trackingResult.na;
  const safeDate = (val) => val ? format(new Date(val), 'EEE, MMM dd, yyyy h:mm a') : t.trackingResult.na;
  const safeDateShort = (val) => val ? format(new Date(val), 'MMM dd, yyyy') : t.trackingResult.na;

  const handlePrint = () => {
    window.print();
  };

  const trackingNo = data.trackingNumber;

  return (
    <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="bg-[#0B1F3A] p-5 sm:p-6 md:p-8 text-white text-center">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">{t.trackingResult.title}</h2>
        <p className="text-gray-300 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
          {t.trackingResult.intro}
        </p>
        <p className="mt-3 font-bold text-[#FF6B00]">{t.trackingResult.detailsBelow}</p>
      </div>

      <div className="p-4 sm:p-6 md:p-8 space-y-6 md:space-y-8">
        {/* URGENT HOLD NOTICE */}
        {data.currentStatus === 'on_hold' && (
          <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 sm:p-5 md:p-6 shadow-md">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 bg-red-600 rounded-full flex items-center justify-center animate-pulse">
                <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-red-800 uppercase tracking-wide">
                  {t.trackingResult.urgentHold}
                </h3>
                <p className="text-red-700 text-xs sm:text-sm md:text-base mt-1 leading-relaxed">
                  {t.trackingResult.holdDesc}
                </p>
              </div>
            </div>
            <div className="bg-white border border-red-200 rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:gap-4 sm:items-center sm:justify-center">
              <a href="tel:+496774420901" className="flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-bold px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg transition-colors text-xs sm:text-sm md:text-base">
                <PhoneIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>Berlin HQ: +49 677 442 0901</span>
              </a>
              <a href="tel:+12134774806" className="flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-bold px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg transition-colors text-xs sm:text-sm md:text-base">
                <PhoneIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>USA: +1 213-477-4806</span>
              </a>
            </div>
          </div>
        )}

        {/* Map & Route */}
        <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
          <div className="bg-[#0B1F3A] text-white px-3 sm:px-4 py-2.5 flex items-center justify-between">
            <span className="font-bold text-xs sm:text-sm md:text-base">{t.trackingResult.liveMap}</span>
            <span className="text-xs text-gray-300 hidden sm:inline">{t.trackingResult.originDestination}</span>
          </div>
          <div className="p-2 sm:p-3">
            <div className="flex items-center gap-2 mb-2 px-1">
              <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00] flex-shrink-0" />
              <p className="text-sm sm:text-base font-semibold text-[#0B1F3A]">
                {safe(data.currentLocation) || safe(data.sender?.location) || t.trackingResult.locationUnavailable}
              </p>
            </div>
            <div className="relative rounded-lg overflow-hidden w-full h-48 sm:h-72 md:h-80 lg:h-96 bg-gray-100">

              <iframe
                title="Shipment Map"
                width="100%"
                frameBorder="0"
                scrolling="no"
                marginHeight="0"
                marginWidth="0"
                
                src={`https://maps.google.com/maps?q=${encodeURIComponent(data.currentLocation || data.sender?.location || 'World')}&t=&z=8&ie=UTF8&iwloc=&output=embed`}
                className="rounded-lg w-full h-48 sm:h-72 md:h-80 lg:h-96 relative z-10 bg-white"
              />
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start space-x-2">
                <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00] flex-shrink-0" />
                <div>
                  <p className="text-gray-500 text-xs uppercase">{t.trackingResult.origin}</p>
                  <p className="font-semibold text-[#17202A] text-sm sm:text-base">{safe(data.sender?.location)}</p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#DC2626] flex-shrink-0" />
                <div>
                  <p className="text-gray-500 text-xs uppercase">{t.trackingResult.destination}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base">{safe(data.recipient?.location)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Barcode & Print */}
        <div className="text-center space-y-3 sm:space-y-4">
          <div className="inline-block w-full max-w-xs border-2 border-gray-300 p-2.5 sm:p-3 md:p-4 rounded-lg bg-white">
            <svg className="w-full max-w-[240px] h-12 sm:h-14 md:h-16 mx-auto" viewBox="0 0 200 50" preserveAspectRatio="xMidYMid meet">
              {[...Array(40)].map((_, i) => {
                const w = Math.random() > 0.5 ? 2 : 4;
                const x = i * 5;
                return <rect key={i} x={x} y="0" width={w} height="50" fill="#000" />;
              })}
            </svg>
            <p className="mt-2 text-xs sm:text-sm font-bold tracking-widest text-gray-800 break-all">{trackingNo}</p>
          </div>
          <button
            onClick={handlePrint}
            className="inline-flex items-center px-5 sm:px-6 py-2 sm:py-2.5 bg-[#FF6B00] hover:bg-[#e65f00] text-white font-medium rounded-lg transition-colors shadow-md text-sm sm:text-base"
          >
            {t.trackingResult.printInvoice}
          </button>
        </div>

        {/* Two-column layout: Receiver + Consignment | Sender + Origin/Destination */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Left column */}
          <div className="space-y-4 sm:space-y-6">
            {/* Receiver's Details */}
            <div>
              <h3 className="bg-[#123B63] text-white font-bold px-3 sm:px-4 py-2 rounded-t-lg text-xs sm:text-sm md:text-base">{t.trackingResult.receiverDetails}</h3>
              <div className="border border-t-0 border-gray-200 rounded-b-lg grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 divide-gray-100">
                <div className="p-3 sm:p-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">{t.trackingResult.fullName}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-words">{safe(data.recipient?.name)}</p>
                </div>
                <div className="p-3 sm:p-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">{t.trackingResult.address}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-words">{safe(data.recipient?.location)}</p>
                </div>
                <div className="p-3 sm:p-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">{t.trackingResult.emailAddress}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-all">{safe(data.recipient?.email)}</p>
                </div>
                <div className="p-3 sm:p-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">{t.trackingResult.phoneNumber}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-words">{safe(data.recipient?.phone)}</p>
                </div>
              </div>
            </div>

            {/* Consignment's Details */}
            <div className="rounded-xl overflow-hidden shadow-md border border-gray-200">
              <h3 className="bg-[#0B1F3A] text-white font-bold px-4 sm:px-5 py-2.5 sm:py-3 text-xs sm:text-sm md:text-base flex items-center space-x-2">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span>{t.trackingResult.consignmentDetails}</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-200 bg-white">
                <div className="p-3 sm:p-4 bg-[#F5F7FA]">
                  <p className="text-xs uppercase text-[#FF6B00] mb-1 font-semibold">{t.trackingResult.trackingNumber}</p>
                  <p className="font-mono font-bold text-[#17202A] break-all text-xs sm:text-sm md:text-base">{trackingNo}</p>
                </div>
                <div className="p-3 sm:p-4 bg-[#F5F7FA]">
                  <p className="text-xs uppercase text-[#FF6B00] mb-1 font-semibold">{t.trackingResult.status}</p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold ${
                    data.currentStatus === 'on_hold' || data.currentStatus === 'exception' || data.currentStatus === 'cancelled' || data.currentStatus === 'returned'
                      ? 'bg-red-100 text-[#DC2626] border border-red-200'
                      : data.currentStatus === 'delivered'
                      ? 'bg-green-100 text-[#16A34A] border border-green-200'
                      : data.currentStatus === 'pending'
                      ? 'bg-amber-100 text-[#F59E0B] border border-amber-200'
                      : 'bg-blue-100 text-[#2563EB] border border-blue-200'
                  }`}>
                    {getStatusIcon(data.currentStatus)}
                    {formatStatus(data.currentStatus)}
                  </span>
                </div>
                <div className="p-3 sm:p-4 bg-white">
                  <p className="text-xs uppercase text-[#64748B] mb-1 font-semibold">{t.trackingResult.packageWeight}</p>
                  <p className="font-bold text-[#17202A] text-sm sm:text-base">{data.packageInfo?.weight ? `${data.packageInfo.weight} kg` : t.trackingResult.na}</p>
                </div>
                <div className="p-3 sm:p-4 bg-white">
                  <p className="text-xs uppercase text-[#64748B] mb-1 font-semibold">{t.trackingResult.serviceType}</p>
                  <p className="font-bold text-[#17202A] capitalize text-sm sm:text-base">{safe(data.serviceType)}</p>
                </div>
                <div className="p-3 sm:p-4 bg-white sm:col-span-2">
                  <p className="text-xs uppercase text-[#64748B] mb-1 font-semibold">{t.trackingResult.deliveryMode}</p>
                  <p className="font-bold text-[#17202A] text-sm sm:text-base">{safe(data.deliveryMode || 'Sea Freight')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-4 sm:space-y-6">
            {/* Sender's Details */}
            <div>
              <h3 className="bg-[#123B63] text-white font-bold px-3 sm:px-4 py-2 rounded-t-lg text-xs sm:text-sm md:text-base">{t.trackingResult.senderDetails}</h3>
              <div className="border border-t-0 border-gray-200 rounded-b-lg grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 divide-gray-100">
                <div className="p-3 sm:p-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">{t.trackingResult.senderName}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-words">{safe(data.sender?.name)}</p>
                </div>
                <div className="p-3 sm:p-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">{t.trackingResult.address}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-words">{safe(data.sender?.location)}</p>
                </div>
                <div className="p-3 sm:p-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">{t.trackingResult.senderEmail}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-all">{safe(data.sender?.email)}</p>
                </div>
                <div className="p-3 sm:p-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">{t.trackingResult.phoneNumber}</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-words">{safe(data.sender?.phone)}</p>
                </div>
              </div>
            </div>

            {/* Origin / Destination / Dates */}
            <div className="rounded-xl overflow-hidden shadow-md border border-gray-200">
              <h3 className="bg-[#0B1F3A] text-white font-bold px-4 sm:px-5 py-2.5 sm:py-3 text-xs sm:text-sm md:text-base flex items-center space-x-2">
                <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00]" />
                <span>{t.trackingResult.shipmentRoute}</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-200 bg-white">
                <div className="p-3 sm:p-4 bg-[#F5F7FA]">
                  <p className="text-xs uppercase text-[#FF6B00] mb-1 font-semibold">{t.trackingResult.origin}</p>
                  <p className="font-bold text-[#17202A] text-sm sm:text-base break-words">{safe(data.sender?.location)}</p>
                </div>
                <div className="p-3 sm:p-4 bg-[#F5F7FA]">
                  <p className="text-xs uppercase text-[#FF6B00] mb-1 font-semibold">{t.trackingResult.destination}</p>
                  <p className="font-bold text-[#17202A] text-sm sm:text-base break-words">{safe(data.recipient?.location)}</p>
                </div>
                <div className="p-3 sm:p-4 bg-white">
                  <p className="text-xs uppercase text-[#64748B] mb-1 font-semibold">{t.trackingResult.pickUpDate}</p>
                  <p className="font-bold text-[#17202A] text-xs sm:text-sm md:text-base">{safeDate(data.shippedDate)}</p>
                </div>
                <div className="p-3 sm:p-4 bg-white">
                  <p className="text-xs uppercase text-[#64748B] mb-1 font-semibold">{t.trackingResult.expectedDelivery}</p>
                  <p className="font-bold text-[#17202A] text-xs sm:text-sm md:text-base">{safeDateShort(data.estimatedDelivery)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <ShipmentProgressBar
          currentStatus={data.currentStatus}
          senderLocation={data.sender?.location}
          recipientLocation={data.recipient?.location}
          progressPercentage={data.progressPercentage}
        />

        {/* Tracking History */}
        <div>
          <h3 className="bg-[#0B1F3A] text-white font-bold px-3 sm:px-4 py-2 rounded-t-lg text-xs sm:text-sm md:text-base">{t.trackingResult.trackingHistory}</h3>
          <div className="overflow-x-auto border border-gray-200 rounded-b-lg">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-2 sm:px-3 py-2 text-left font-semibold">{t.trackingResult.sNo}</th>
                  <th className="px-2 sm:px-3 py-2 text-left font-semibold">{t.trackingResult.currentLocation}</th>
                  <th className="px-2 sm:px-3 py-2 text-left font-semibold">{t.trackingResult.facility}</th>
                  <th className="px-2 sm:px-3 py-2 text-left font-semibold">{t.trackingResult.arrivalCountry}</th>
                  <th className="px-2 sm:px-3 py-2 text-left font-semibold whitespace-nowrap">{t.trackingResult.dateTime}</th>
                  <th className="px-2 sm:px-3 py-2 text-left font-semibold">{t.trackingResult.comments}</th>
                </tr>
              </thead>
              <tbody>
                {data.trackingHistory.map((event, index) => (
                  <tr key={index} className={`border-t border-gray-300 ${
                    index === 0
                      ? 'bg-orange-50 border-l-4 border-l-[#FF6B00] font-medium'
                      : index % 2 === 0 ? 'bg-[#F5F7FA]' : 'bg-white'
                  }`}>
                    <td className="px-2 sm:px-3 py-2 text-gray-700">
                      {index === 0 ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 bg-[#FF6B00] text-white rounded-full text-[10px] sm:text-xs font-bold">1</span>
                      ) : (
                        index + 1
                      )}
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-gray-800">
                      {index === 0 ? (
                        <span className="flex items-center space-x-1.5">
                          <MapPinIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#FF6B00] flex-shrink-0" />
                          <span className="font-bold text-[#0B1F3A]">{safe(event.location)}</span>
                        </span>
                      ) : (
                        safe(event.location)
                      )}
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-gray-800">{safe(event.facilityName)}</td>
                    <td className="px-2 sm:px-3 py-2 text-gray-800">{safe(event.arrivalCountry)}</td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-gray-800">{safeDate(event.timestamp)}</td>
                    <td className="px-2 sm:px-3 py-2 text-gray-800">{safe(event.description || event.notes)}</td>
                  </tr>
                ))}
                {data.trackingHistory.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-3 py-4 text-center text-gray-500 text-sm">{t.trackingResult.noHistory}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingResult;
